import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Flame, Zap, BookOpen, Terminal, ArrowRight, Target, Swords, Map as MapIcon, Layers, Trophy } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CourseCard from "@/components/CourseCard";
import AdBanner from "@/components/AdBanner";

const TOOLTIP = {
  contentStyle: { background: "#0F1626", border: "1px solid #334155", borderRadius: 12, color: "#E2E8F0" },
  itemStyle: { color: "#E2E8F0" },
  labelStyle: { color: "#94A3B8" },
  cursor: { fill: "rgba(148,163,184,0.08)" },
};

const QUICK = [
  { to: "/playground", testid: "goto-playground", icon: Terminal, title: "Playground", text: "Code frei ausprobieren", color: "text-primary" },
  { to: "/challenges", testid: "goto-challenges", icon: Swords, title: "Challenges", text: "Inkl. Tages-Challenge", color: "text-secondary" },
  { to: "/lernpfad", testid: "goto-lernpfad", icon: MapIcon, title: "Lernpfad", text: "Dein Schaltplan aller Kurse", color: "text-accent" },
  { to: "/flashcards", testid: "goto-flashcards", icon: Layers, title: "Flashcards", text: "Wiederholen mit Spaced Repetition", color: "text-warning" },
];

function Stat({ icon: Icon, color, value, label, testid }) {
  return (
    <div className="nb-card p-5">
      <Icon className={`h-6 w-6 mb-3 ${color}`} strokeWidth={2.2} />
      <div className="font-mono text-3xl font-semibold text-foreground" data-testid={testid}>{value}</div>
      <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  const completed = progress?.completed_count ?? 0;
  const total = progress?.total_lessons ?? 0;
  const overallPct = total ? Math.round((completed / total) * 100) : 0;
  const startedCourses = courses.filter((c) => (progress?.per_course?.[c.slug]?.done ?? 0) > 0);
  // Most advanced unfinished course first: that's where "Weiterlernen" should lead.
  const resume = startedCourses
    .filter((c) => (progress?.per_course?.[c.slug]?.done ?? 0) < (progress?.per_course?.[c.slug]?.total ?? c.lesson_count))
    .sort((a, b) => (progress.per_course[b.slug].done / progress.per_course[b.slug].total) - (progress.per_course[a.slug].done / progress.per_course[a.slug].total))[0];

  const quizzesCorrect = Object.values(progress?.quiz_scores ?? {}).filter(Boolean).length;
  const activityData = [
    { name: "Lektionen", value: completed, color: "hsl(var(--primary))" },
    { name: "Quizze", value: quizzesCorrect, color: "hsl(var(--secondary))" },
    { name: "Challenges", value: progress?.challenges_solved ?? 0, color: "hsl(var(--accent))" },
  ].filter((d) => d.value > 0);

  const courseChartData = courses.map((c) => ({
    name: c.title,
    done: progress?.per_course?.[c.slug]?.done ?? 0,
    offen: (progress?.per_course?.[c.slug]?.total ?? c.lesson_count) - (progress?.per_course?.[c.slug]?.done ?? 0),
  }));

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="animate-fade-up">
        <p className="eyebrow mb-1">// Kontrollpult</p>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-foreground">
          Hallo, {user?.name?.split(" ")[0] || "Coder"}!
        </h1>
        <p className="text-muted-foreground mt-1">Bereit für die nächste Leiterbahn?</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={Flame} color="text-warning" value={user?.streak ?? 0} label="Tage in Folge" testid="stat-streak" />
        <Stat icon={Zap} color="text-primary" value={user?.xp ?? 0} label="XP gesammelt" testid="stat-xp" />
        <Stat icon={BookOpen} color="text-secondary" value={`${completed}/${total}`} label="Lektionen" testid="stat-lessons" />
        <div className="nb-card p-5">
          <Target className="h-6 w-6 mb-3 text-accent" strokeWidth={2.2} />
          <div className="font-mono text-3xl font-semibold text-foreground">{overallPct}%</div>
          <div className="mt-2 h-1.5 rounded-full bg-line overflow-hidden">
            <div className="h-full rounded-full trace-on" style={{ width: `${overallPct}%` }} />
          </div>
          <div className="text-sm text-muted-foreground mt-2">Gesamtfortschritt</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="nb-card p-4 text-center">
          <div className="font-mono text-xl font-semibold text-foreground" data-testid="stat-challenges">
            {progress?.challenges_solved ?? 0}/{progress?.total_challenges ?? 0}
          </div>
          <div className="text-xs text-muted-foreground">Challenges</div>
        </div>
        <div className="nb-card p-4 text-center">
          <div className="font-mono text-xl font-semibold text-foreground" data-testid="stat-daily-streak">{progress?.daily_streak ?? 0}</div>
          <div className="text-xs text-muted-foreground">Tages-Streak</div>
        </div>
        <div className="nb-card p-4 text-center">
          <div className="font-mono text-xl font-semibold text-foreground" data-testid="stat-flashcards-due">{progress?.flashcards_due_today ?? 0}</div>
          <div className="text-xs text-muted-foreground">Karten fällig</div>
        </div>
      </div>

      <Link
        to={resume ? `/kurs/${resume.slug}` : "/kurse"}
        data-testid="goto-courses"
        className="relative flex flex-wrap items-center justify-between gap-4 overflow-hidden rounded-2xl border border-primary/70 bg-card p-6 sm:p-7 glow nb-press"
      >
        <span className="absolute inset-x-0 top-0 h-0.5 trace-flow" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-mono text-xs tracking-widest text-primary">
            {resume ? `WEITER LERNEN · ${resume.title.toUpperCase()}` : "LOSLEGEN"}
          </p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-foreground mt-1">
            {resume ? `${progress.per_course[resume.slug].done} von ${progress.per_course[resume.slug].total} Lektionen bestromt` : "Wähle deinen ersten Kurs"}
          </h2>
          <p className="text-muted-foreground mt-1">{resume ? "Mach dort weiter, wo du aufgehört hast." : "14 Kurse warten auf dich."}</p>
        </div>
        <span className="flex items-center gap-2 rounded-xl bg-primary px-5 py-3 font-semibold">
          {resume ? "Weiterlernen" : "Kurse ansehen"}
          <ArrowRight className="h-5 w-5" strokeWidth={2.2} />
        </span>
      </Link>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {QUICK.map(({ to, testid, icon: Icon, title, text, color }) => (
          <Link key={to} to={to} data-testid={testid} className="nb-card nb-press p-5 flex flex-col gap-2">
            <Icon className={`h-6 w-6 ${color}`} strokeWidth={2.2} />
            <h3 className="font-heading font-semibold text-lg text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground">{text}</p>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="nb-card p-5" data-testid="activity-chart">
          <h3 className="font-heading font-semibold text-lg mb-3 text-foreground">Aktivität nach Bereich</h3>
          {activityData.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Aktivität – leg los!</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={activityData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={82} paddingAngle={3}>
                  {activityData.map((d, i) => (
                    <Cell key={i} fill={d.color} stroke="#0F1626" strokeWidth={3} />
                  ))}
                </Pie>
                <Tooltip {...TOOLTIP} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="nb-card p-5" data-testid="course-progress-chart">
          <h3 className="font-heading font-semibold text-lg mb-3 text-foreground">Fortschritt pro Kurs</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={courseChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94A3B8" }} stroke="#334155" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} stroke="#334155" />
              <Tooltip {...TOOLTIP} />
              <Bar dataKey="done" stackId="a" fill="hsl(var(--primary))" name="Erledigt" />
              <Bar dataKey="offen" stackId="a" fill="#1E293B" name="Offen" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="h-5 w-5 text-primary" strokeWidth={2.2} />
          <h2 className="font-heading font-bold text-2xl text-foreground">Deine Kurse</h2>
        </div>
        {progress && startedCourses.length === 0 ? (
          <div className="nb-card p-6 flex flex-col items-center text-center gap-3" data-testid="no-started-courses">
            <p className="font-semibold text-foreground">Du hast noch keinen Kurs begonnen.</p>
            <p className="text-sm text-muted-foreground">Schau dich auf der Kursübersicht um und starte deine erste Lektion.</p>
            <Link to="/kurse" data-testid="dashboard-goto-courses-empty" className="inline-flex items-center gap-2 bg-primary font-semibold px-4 py-2.5 rounded-lg">
              Kurse entdecken
              <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {startedCourses.map((c) => (
              <CourseCard key={c.slug} course={c} progress={progress?.per_course?.[c.slug]} />
            ))}
            <AdBanner field="dashboard_snippet" className="nb-card p-4 min-h-[180px] w-full" />
          </div>
        )}
      </div>
    </div>
  );
}
